import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { FullScreenDialog } from "./FullScreenDialog";
import { EditSubscriptionAdditionalConfigurationItem } from "./EditSubscriptionAdditionalConfigurationItem";
import { TwilioSubscriptionAdditionalConfiguration, TwilioEntryConfiguration } from "../models/Subscription";
import Button from "@material-ui/core/Button";
import nanoid from "nanoid";
import { Typography, FormControl, FormLabel, TextField, Box, Paper } from "@material-ui/core";
import { updateTwilioSubscriptionConfiguration } from "../services/api/subscription.api";
import { useSnackbar } from "notistack";
import { DEFAULT_TWILIO_SUBSCRIPTION_CONFIG, ENVIRONMENTS } from "../constants";
import { showSnack } from "../utils/component";
import colors from "../constants/colors";

interface EditAdditionalSubscriptionConfigProps {
	// list of configurations
	config: TwilioSubscriptionAdditionalConfiguration;
	// subscription being configured
	subscriptionId: string;
	// product id used to fetch the subscription
	productId: string;
	// function to call when the form is successfully saved,
	// can be used by the parent component to update it's data
	done: () => void;
}

const useStyles = makeStyles((theme) => ({
	container: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
	},
	actions: {
		marginLeft: theme.spacing(2),
	},
	errorWrapper: {
		padding: theme.spacing(3),
		marginBottom: theme.spacing(3),
	},
	formControl: {
		marginLeft: theme.spacing(1),
		marginRight: theme.spacing(1),
		marginBottom: theme.spacing(2),
		maxWidth: 996,
		width: "100%",
	},
	formControlLabel: {
		fontSize: theme.typography.caption.fontSize,
		margin: 0,
		textTransform: "uppercase",
		color: colors.gray600,
	},
	inputField: {
		width: 300,
		marginTop: theme.spacing(1),
	},
	appInsightsContainer: {
		display: "flex",
		flexWrap: "wrap",
		width: "100%",
		maxWidth: 996,
		marginBottom: theme.spacing(4),
		padding: theme.spacing(3, 3, 0, 3),
		backgroundColor: theme.palette.background.paper,
		boxShadow: theme.shadows[1],
		position: "relative",
	},
	titleWrapper: {
		display: "flex",
		maxWidth: 998,
		marginBottom: 16,
		width: "100%",
		justifyContent: "space-between",
	},
}));

const validateConfiguration = (configs) => {
	const uniqueAccounts: string[] = [];

	if (!configs || configs.length === 0) {
		return false;
	}

	for (const item of configs) {
		// check required fields
		if (item.Environment === undefined || !item.AccountSid || !item.TwilioApiKeySid || !item.TwilioApiKeySecret) {
			return false;
		}

		// combination of env and account sid has to be unique
		if (uniqueAccounts.indexOf(item.AccountSid) !== -1) {
			return false;
		}

		uniqueAccounts.push(item.AccountSid);
	}

	return true;
};

/**
 * Subscription additional configuration editor
 * @param props
 */
export const EditAdditionalSubscriptionConfig = (props: EditAdditionalSubscriptionConfigProps) => {
	const classes = useStyles();
	const populateConfigObject = (configs: TwilioEntryConfiguration[]) =>
		(configs || []).map((c) => ({ ...c, IsLogLevelOverrideEnabled: !!c.IsLogLevelOverrideEnabled, _id: nanoid() }));

	const [configs, setConfigs] = useState(populateConfigObject(props.config.SubscriptionConfigurations));
	const [appInsightsName, setAppInsightsName] = useState(props.config.ApplicationInsightsName);
	const [piiPropertyNames, setPiiPropertyNames] = useState(props.config.PIIRemovalPropertyNamesCsv);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isValid, setIsValid] = useState(true);
	const [isServerError, setIsServerError] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const [isConfigEditFormOpen, setIsConfigEditFormOpen] = React.useState(false);

	const addConfig = (newConfig) => setConfigs([...configs, { ...newConfig, _id: nanoid() }]);

	const removeConfig = (id) => setConfigs(configs.filter((c) => c._id !== id));

	const updateConfig = (updatedConfig) =>
		setConfigs(configs.map((c) => (c._id === updatedConfig._id ? { ...c, ...updatedConfig } : c)));

	const submitConfigChanges = async (e: React.SyntheticEvent) => {
		e.preventDefault();

		setIsValid(true);
		setIsServerError(false);
		if (!validateConfiguration(configs)) {
			setIsValid(false);
			return;
		}

		setIsSubmitting(true);

		try {
			await updateTwilioSubscriptionConfiguration(props.productId, props.subscriptionId, {
				SubscriptionConfigurations: configs,
				ApplicationInsightsName: appInsightsName,
				PIIRemovalPropertyNamesCsv: piiPropertyNames,
			})();

			showSnack(enqueueSnackbar, "Changes saved successfully");

			setIsSubmitting(false);
			setIsConfigEditFormOpen(false);
			props.done();
		} catch (error) {
			console.log(error);
			setIsServerError(true);
			setIsSubmitting(false);
		}
	};

	const actions = (
		<div>
			<Button
				data-testid="edit-additional-subscription-config-save-btn"
				disabled={isSubmitting}
				className={classes.actions}
				variant="contained"
				color="primary"
				onClick={submitConfigChanges}
			>
				Save changes
			</Button>
		</div>
	);
	return (
		<FullScreenDialog
			label="Edit configuration"
			title="Edit Subscription Additional Configuration"
			actions={actions}
			isOpen={isConfigEditFormOpen}
			emitState={(isOpen) => {
				setIsConfigEditFormOpen(isOpen);

				if (!isOpen) {
					setConfigs(populateConfigObject(props.config.SubscriptionConfigurations));
					setAppInsightsName(props.config.ApplicationInsightsName);
					setPiiPropertyNames(props.config.PIIRemovalPropertyNamesCsv);
					setIsServerError(false);
					setIsSubmitting(false);
					setIsValid(true);
				}
			}}
		>
			<div className={classes.container}>
				{(!isValid || isServerError) && (
					<Paper className={classes.errorWrapper}>
						<Typography
							component="div"
							className="configuration"
							color="error"
							variant="body1"
							data-testid="edit-additional-subscription-config-validation-message"
						>
							{!isValid && (
								<div>
									<p>
										<strong>Validation Error: </strong> Please make sure all environment configurations meet the
										following criteria.
									</p>
									<ul>
										<li>At least one configuration exists</li>
										<li>Environment is not blank</li>
										<li>Account SID is not blank</li>
										<li>Twilio Api Key Sid is not blank</li>
										<li>Twilio Api Key Secret is not blank</li>
										<li>There are no duplicate Account SIDs</li>
									</ul>
								</div>
							)}
							{isServerError && <span>An error occurred, please try again.</span>}
						</Typography>
					</Paper>
				)}
				<div className={classes.titleWrapper}>
					<Typography variant="h6">General Settings</Typography>
				</div>
				<Box className={classes.appInsightsContainer}>
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>Application Insights Name</FormLabel>
						<TextField
							className={classes.inputField}
							value={appInsightsName}
							margin="normal"
							onChange={(e) => setAppInsightsName(e.target.value)}
						/>
					</FormControl>
					<FormControl className={classes.formControl}>
						<FormLabel className={classes.formControlLabel}>PII Property Names (comma-separated list)</FormLabel>
						<TextField
							className={classes.inputField}
							value={piiPropertyNames}
							margin="normal"
							style={{ width: "auto" }}
							multiline
							onChange={(e) => setPiiPropertyNames(e.target.value)}
						/>
					</FormControl>
				</Box>
				<div className={classes.titleWrapper}>
					<Typography variant="h6">Environment Configurations</Typography>
					<div>
						<div>
							<Button
								data-testid="edit-additional-subscription-config-add-new-btn"
								size="small"
								variant="outlined"
								color="primary"
								onClick={() => addConfig(DEFAULT_TWILIO_SUBSCRIPTION_CONFIG)}
							>
								Add configuration
							</Button>
						</div>
					</div>
				</div>

				{configs.map((c) => (
					<EditSubscriptionAdditionalConfigurationItem
						key={c._id}
						environments={ENVIRONMENTS}
						config={c}
						onRemove={removeConfig}
						onUpdate={updateConfig}
					/>
				))}
			</div>
		</FullScreenDialog>
	);
};
